/**
 * Merkzettel (wishlist) — client-side, localStorage-only.
 *
 * Single source of truth for the saved-products list. Works for guests and
 * logged-in customers under the no-app / no-backend constraint:
 *   - guests       -> key "wishlist:guest"
 *   - logged-in    -> key "wishlist:cust:<customerId>"
 * On login the guest list is merged (union) into the customer list once, then
 * the guest key is cleared. Cross-device sync is intentionally out of scope.
 *
 * Configuration is provided by snippets/wishlist-data.liquid as
 * window.WISHLIST_CONFIG = { customerId, page, strings }.
 *
 * UI is driven entirely by data attributes so the same code handles Liquid
 * card surfaces and the Vue-rendered collection grid without a second store:
 *   <button data-wishlist-toggle
 *           data-wishlist-id="123"
 *           data-wishlist-title="..."
 *           data-wishlist-url="/products/..."
 *           data-wishlist-image="https://...">
 */
(function () {
  'use strict';

  var CONFIG = window.WISHLIST_CONFIG || {};
  var STRINGS = CONFIG.strings || {};
  var GUEST_KEY = 'wishlist:guest';
  var EVENT = 'wishlist:changed';

  function customerKey() {
    var id = CONFIG.customerId;
    return id ? 'wishlist:cust:' + id : null;
  }

  function activeKey() {
    return customerKey() || GUEST_KEY;
  }

  function safeParse(raw) {
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function readKey(key) {
    if (!key) return [];
    return safeParse(localStorage.getItem(key));
  }

  function writeKey(key, items) {
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch (e) {
      /* quota / private mode — fail silently, feature degrades gracefully */
    }
  }

  function normalizeId(id) {
    return String(id == null ? '' : id);
  }

  /**
   * A wishlist id is valid only when it is a Shopify numeric product id — the id
   * the Liquid surfaces (`wishlist-toggle.liquid`) and the Vue grid both emit.
   * Shopify product ids are long numerics (13-14 digits on this store, e.g.
   * 10881992360203); the external backend search id that early builds of the grid
   * stored is a short sequential integer (e.g. 3884). A digits-only test with a
   * generous length floor cleanly separates the two without false-dropping any
   * real Shopify id. Used by the one-time legacy prune below.
   */
  function isShopifyShapedId(id) {
    return /^\d{10,}$/.test(normalizeId(id));
  }

  var Wishlist = {
    /** @returns {Array<{id,title,url,image}>} the active list */
    get: function () {
      return readKey(activeKey());
    },

    ids: function () {
      return this.get().map(function (item) {
        return item.id;
      });
    },

    count: function () {
      return this.get().length;
    },

    has: function (id) {
      id = normalizeId(id);
      return this.get().some(function (item) {
        return item.id === id;
      });
    },

    add: function (product) {
      if (!product || product.id == null) return this.get();
      var items = this.get();
      var id = normalizeId(product.id);
      if (
        !items.some(function (item) {
          return item.id === id;
        })
      ) {
        items.push({
          id: id,
          title: product.title || '',
          url: product.url || '',
          image: product.image || ''
        });
        writeKey(activeKey(), items);
        emit();
      }
      return items;
    },

    remove: function (id) {
      id = normalizeId(id);
      var items = this.get().filter(function (item) {
        return item.id !== id;
      });
      writeKey(activeKey(), items);
      emit();
      return items;
    },

    toggle: function (product) {
      if (!product || product.id == null) return false;
      if (this.has(product.id)) {
        this.remove(product.id);
        return false;
      }
      this.add(product);
      return true;
    },

    clear: function () {
      writeKey(activeKey(), []);
      emit();
    },

    /**
     * Merge the anonymous guest list into the customer list once on login,
     * then clear the guest key. No-op for guests or when there is nothing to
     * merge.
     */
    migrateGuestToCustomer: function () {
      var custKey = customerKey();
      if (!custKey) return;
      var guestItems = readKey(GUEST_KEY);
      if (!guestItems.length) return;
      var custItems = readKey(custKey);
      var seen = {};
      custItems.forEach(function (item) {
        seen[item.id] = true;
      });
      guestItems.forEach(function (item) {
        if (!seen[item.id]) {
          custItems.push(item);
          seen[item.id] = true;
        }
      });
      writeKey(custKey, custItems);
      localStorage.removeItem(GUEST_KEY);
      emit();
    },

    /**
     * One-time cleanup of entries saved under the old backend search id (before
     * the Vue grid was fixed to emit the Shopify product id). Such entries can
     * never match a toggle on any surface, so their heart never fills and — worse
     * — re-saving the same product from a fixed surface would create a duplicate.
     * Dropping them restores the "at most once" guarantee. Idempotent: after the
     * first pass nothing non-Shopify-shaped remains, so later loads are no-ops.
     */
    pruneLegacyIds: function () {
      var key = activeKey();
      var items = readKey(key);
      if (!items.length) return;
      var kept = items.filter(function (item) {
        return item && isShopifyShapedId(item.id);
      });
      if (kept.length !== items.length) {
        writeKey(key, kept);
        emit();
      }
    }
  };

  function emit() {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { count: Wishlist.count() } }));
  }

  // ---- UI wiring -----------------------------------------------------------

  function productFromToggle(el) {
    return {
      id: el.getAttribute('data-wishlist-id'),
      title: el.getAttribute('data-wishlist-title') || '',
      url: el.getAttribute('data-wishlist-url') || '',
      image: el.getAttribute('data-wishlist-image') || ''
    };
  }

  function applyToggleState(el) {
    var id = el.getAttribute('data-wishlist-id');
    var saved = Wishlist.has(id);
    el.setAttribute('aria-pressed', saved ? 'true' : 'false');
    el.classList.toggle('is-saved', saved);
    var label = saved ? STRINGS.remove : STRINGS.add;
    if (label) el.setAttribute('aria-label', label);
    var icon = el.querySelector('[data-wishlist-icon]');
    if (icon) {
      // Font Awesome: solid (fas) when saved, regular (far) when not.
      icon.classList.toggle('fas', saved);
      icon.classList.toggle('far', !saved);
    }
  }

  function syncAllToggles(root) {
    var scope = root || document;
    var toggles = scope.querySelectorAll('[data-wishlist-toggle]');
    Array.prototype.forEach.call(toggles, applyToggleState);
  }

  function onToggleClick(e) {
    var el = e.target.closest && e.target.closest('[data-wishlist-toggle]');
    if (!el) return;
    e.preventDefault();
    Wishlist.toggle(productFromToggle(el));
    applyToggleState(el);
  }

  function updateHeaderCount(count) {
    var nodes = document.querySelectorAll('[data-wishlist-count]');
    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = count;
      node.classList.toggle('wishlist-count--empty', count === 0);
    });
  }

  // ---- Wishlist page renderer ---------------------------------------------

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[c];
    });
  }

  function renderWishlistPage() {
    var container = document.getElementById('wishlist-page');
    if (!container) return;
    var items = Wishlist.get();

    if (!items.length) {
      container.innerHTML =
        '<p class="text-center text-gray-40 py-10" data-wishlist-empty>' +
        escapeHtml(STRINGS.empty || '') +
        '</p>';
      return;
    }

    var html = items
      .map(function (item) {
        var url = escapeHtml(item.url);
        var title = escapeHtml(item.title);
        var image = escapeHtml(item.image);
        return (
          '<div class="flex items-center border border-gray-20 p-4 gap-4" data-wishlist-row data-wishlist-row-id="' +
          escapeHtml(item.id) +
          '">' +
          '<a href="' +
          url +
          '" class="shrink-0 w-20 h-20 flex items-center justify-center">' +
          (image
            ? '<img src="' + image + '" alt="' + title + '" class="max-w-full max-h-full">'
            : '') +
          '</a>' +
          '<a href="' +
          url +
          '" class="flex-1 text-gray-40 hover:text-green-30 font-bold">' +
          title +
          '</a>' +
          '<button type="button" data-wishlist-remove="' +
          escapeHtml(item.id) +
          '" class="text-gray-30 hover:text-red-20 transition-all duration-250 p-2" aria-label="' +
          escapeHtml(STRINGS.remove || '') +
          '">' +
          '<i class="fa fa-trash"></i>' +
          '</button>' +
          '</div>'
        );
      })
      .join('');

    container.innerHTML = '<div class="space-y-4">' + html + '</div>';
  }

  function onPageRemoveClick(e) {
    var btn = e.target.closest && e.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    e.preventDefault();
    Wishlist.remove(btn.getAttribute('data-wishlist-remove'));
  }

  // ---- Vue grid re-render handling ----------------------------------------

  function observeDynamicGrids() {
    var grid = document.getElementById('vuejs_cannabisapo');
    if (!grid || typeof MutationObserver === 'undefined') return;
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      // Vue mutates the DOM in batches; coalesce to a single sync.
      window.requestAnimationFrame(function () {
        pending = false;
        syncAllToggles(grid);
      });
    });
    observer.observe(grid, { childList: true, subtree: true });
  }

  // ---- Bootstrap -----------------------------------------------------------

  function init() {
    Wishlist.migrateGuestToCustomer();
    Wishlist.pruneLegacyIds();

    document.addEventListener('click', onToggleClick);
    document.addEventListener('click', onPageRemoveClick);

    document.addEventListener(EVENT, function (e) {
      var count = (e.detail && e.detail.count) != null ? e.detail.count : Wishlist.count();
      updateHeaderCount(count);
      syncAllToggles();
      renderWishlistPage();
    });

    updateHeaderCount(Wishlist.count());
    syncAllToggles();
    renderWishlistPage();
    observeDynamicGrids();
  }

  // Expose for the Vue grid / debugging / other inline scripts.
  window.Wishlist = Wishlist;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
